import {
  createShardBackedDesignOntologyReleaseResolver,
  findDesignBindingShardRecord,
} from "../design-ontology-binding/shard-backed-release-resolver"
import { stableJsonSha256 } from "../design-ontology-binding/stable-json-hash"
import {
  NEWSLETTER_MEMBERSHIP_APPLICATION_BLUEPRINT,
  NEWSLETTER_MEMBERSHIP_DESIGNATION,
  NEWSLETTER_MEMBERSHIP_LIBRARY_AUTHORITY,
  NEWSLETTER_MEMBERSHIP_UX_PATTERNS,
} from "../library-composition/newsletter-membership-library-designation"
import type {
  LibraryKnowledgeBundle,
  LibraryKnowledgeSummary,
  LibraryRagOrchestrationRequest,
} from "../library-rag-agent-orchestration/types"
import {
  hashPlatformCanonicalJson,
  parsePlatformLibraryKnowledgeBundleV1,
  type PlatformLibraryKnowledgeBundleV1,
} from "./platform-library-rag-contract"
import { instantiateCanonicalNewsletterSourceInteractionRelationSet } from "./source-interaction-relation-set-authority"
import type {
  LibraryKnowledgeBundleProvenance,
  LibraryKnowledgeShardEnvelope,
} from "./types"

const BLUEPRINT_SHARD_REF =
  "packages/cogo-kg-browser/fixtures/newsletter-membership-application-blueprint-v1.ttl"
const DESIGN_SHARD_REF =
  "packages/cogo-kg-browser/fixtures/design-binding-v1.ttl"
const UX_LIBRARY_SHARD_REF =
  "packages/cogo-kg-browser/fixtures/ux-application-library-v1.ttl"

/**
 * The platform response carries compact graph projections, not the rich TS
 * compiler payloads. This manifest is the only bridge between those forms: a
 * compact asset is admitted only when its exact ref/source/schema and the raw
 * SHA-256 of the canonical cogo-web TTL agree. It is deliberately static and
 * drift-locked by source review/tests; no prompt, title, label or model output
 * participates in the mapping.
 */
export const CANONICAL_PLATFORM_LIBRARY_ASSET_BINDING_MANIFEST = deepFreeze({
  schemaVersion: "cogo.platform-library-asset-binding-manifest.v1" as const,
  applicationBlueprint: {
    ref: "newsletter-membership@1.0.0",
    schemaVersion: "cogo.application-blueprint.v1",
    sourceRef: "cogo:application-core/newsletter-membership-v1",
    ttlShardRef: BLUEPRINT_SHARD_REF,
    rawContentHash:
      "sha256:da263824026c843fb612dfb8c197961108d3e2e530f553ad121a07f1175efe87",
  },
  uxPatterns: [
    [
      "newsletter-member-content@1.0.0",
      "cogo:approved-source/newsletter-member-content-v1",
    ],
    [
      "newsletter-membership-signup@1.0.0",
      "cogo:approved-source/newsletter-membership-signup-v1",
    ],
    [
      "newsletter-operator-impact-controls@1.0.0",
      "cogo:approved-source/newsletter-operator-impact-controls-v1",
    ],
    [
      "newsletter-operator-overview@1.0.0",
      "cogo:approved-source/newsletter-operator-overview-v1",
    ],
  ].map(([ref, sourceRef]) => ({
    ref,
    schemaVersion: "cogo.ux-pattern-definition.v1",
    sourceRef,
    ttlShardRef: UX_LIBRARY_SHARD_REF,
    rawContentHash:
      "sha256:d97494014739c50607bf2f4a9716e82dd841eb5a4638dfd91b7e446fe718b147",
  })),
  designOntology: {
    ref: "library-baseline-web-v1",
    schemaVersion: "cogo.design-composition-release.v1",
    sourceRef: "urn:cogo:approved-source:library-baseline-web-v1",
    ttlShardRef: DESIGN_SHARD_REF,
    rawContentHash:
      "sha256:c23fa6acc011cf930fb7ee680d52fd106702abc6ccc2bdba05c6d24504c8e74c",
  },
})

export async function admitPlatformLibraryKnowledgeBundle(
  input: unknown,
  request: LibraryRagOrchestrationRequest,
): Promise<{
  compact: PlatformLibraryKnowledgeBundleV1
  rich: LibraryKnowledgeBundle<LibraryKnowledgeBundleProvenance>
}> {
  const compact = await parsePlatformLibraryKnowledgeBundleV1(input)
  assertExactRequest(compact, request)
  assertManifestBinding(compact)

  const expectedRelation =
    instantiateCanonicalNewsletterSourceInteractionRelationSet(
      request.applicationInstanceId,
    )
  if (
    (await hashPlatformCanonicalJson(compact.sourceInteractionRelationSet)) !==
    (await hashPlatformCanonicalJson(expectedRelation))
  ) {
    throw new Error("platform-library-interaction-authority-mismatch")
  }

  const releaseRecord = findDesignBindingShardRecord(
    NEWSLETTER_MEMBERSHIP_APPLICATION_BLUEPRINT.applicationCoreRef,
  )
  if (
    !releaseRecord ||
    releaseRecord.designOntologyReleaseRef !== compact.designOntology.releaseRef
  ) {
    throw new Error("platform-library-design-binding-mismatch")
  }
  const release =
    await createShardBackedDesignOntologyReleaseResolver().resolveDesignOntologyRelease(
      compact.designOntology.releaseRef,
    )
  if (!release || release.releaseId !== compact.designOntology.releaseRef) {
    throw new Error("platform-library-design-release-unavailable")
  }

  const patternByRef = new Map(
    NEWSLETTER_MEMBERSHIP_UX_PATTERNS.map((pattern) => [
      `${pattern.uxPatternId}@${pattern.version}`,
      pattern,
    ]),
  )
  const uxPatterns = compact.uxPatterns.map((asset) => {
    const payload = patternByRef.get(asset.ref)
    if (!payload) throw new Error("platform-library-ux-payload-unavailable")
    return {
      summary: summary({
        kind: "ux-pattern",
        ref: asset.ref,
        title: payload.intent,
        detail: `UX Pattern ${asset.ref} admitted from ${asset.sourceRef}.`,
        evidenceRefs: payload.lineage.sourceRefs,
      }),
      payload: structuredClone(payload),
    }
  })
  const applicationBlueprints = [
    {
      summary: summary({
        kind: "application-blueprint",
        ref: compact.applicationBlueprint.ref,
        title: NEWSLETTER_MEMBERSHIP_APPLICATION_BLUEPRINT.userGoal,
        detail: `Application Blueprint ${compact.applicationBlueprint.ref} admitted from ${compact.applicationBlueprint.sourceRef}.`,
        evidenceRefs:
          NEWSLETTER_MEMBERSHIP_APPLICATION_BLUEPRINT.lineage.sourceRefs,
      }),
      payload: structuredClone(NEWSLETTER_MEMBERSHIP_APPLICATION_BLUEPRINT),
    },
  ]
  const designOntologyReleases = [
    {
      summary: summary({
        kind: "design-ontology-release",
        ref: compact.designOntology.releaseRef,
        title: compact.designOntology.releaseRef,
        detail: `Design Ontology release ${compact.designOntology.releaseRef} admitted from ${compact.designOntology.sourceRef}.`,
        evidenceRefs: release.sourceRefs,
      }),
      payload: structuredClone(release),
    },
  ]
  const sourceInteractionRelationSets = [
    {
      summary: summary({
        kind: "source-interaction-relation-set",
        ref: expectedRelation.relationSetId,
        title: expectedRelation.relationSetId,
        detail: "Canonical source-bound newsletter interaction authority.",
        evidenceRefs: expectedRelation.evidenceRefs,
      }),
      payload: structuredClone(expectedRelation),
    },
  ]
  const authorityContext = structuredClone(
    NEWSLETTER_MEMBERSHIP_LIBRARY_AUTHORITY,
  )
  const bundleBody = {
    target: request.target,
    locale: request.locale,
    authorityContext,
    applicationBlueprints,
    uxPatterns,
    designOntologyReleases,
    sourceInteractionRelationSets,
  }
  const bundleContentHash = await stableJsonSha256(bundleBody)
  const retrievalTraceId = `platform-library:${compact.contentHash.slice(7, 39)}`
  const shards: LibraryKnowledgeShardEnvelope[] = [
    {
      shardRef: BLUEPRINT_SHARD_REF,
      admissionGate: "admitUxApplicationLibraryOntology",
      root: {
        kind: "application-blueprint",
        ref: compact.applicationBlueprint.ref,
      },
      assetRefs: [
        compact.applicationBlueprint.ref,
        ...compact.uxPatterns.map((asset) => asset.ref),
      ],
      contentHash: compact.applicationBlueprint.contentHash,
    },
    {
      shardRef: DESIGN_SHARD_REF,
      admissionGate: "admitDesignBindingOntology",
      root: {
        kind: "design-ontology-release",
        ref: compact.designOntology.releaseRef,
      },
      assetRefs: [compact.designOntology.releaseRef],
      contentHash: compact.designOntology.contentHash,
    },
  ]
  const provenance: LibraryKnowledgeBundleProvenance = {
    schemaVersion: "cogo.library-knowledge-bundle-provenance.v1",
    retrievalTraceId,
    shards,
    designationRecordRefs: [
      NEWSLETTER_MEMBERSHIP_DESIGNATION.admissionRecordRef,
    ],
    bundleContentHash,
  }
  return {
    compact,
    rich: deepFreeze({
      schemaVersion: "cogo.library-knowledge-bundle.v1",
      retrievalTraceId,
      ...bundleBody,
      provenance,
    }),
  }
}

function assertExactRequest(
  bundle: PlatformLibraryKnowledgeBundleV1,
  request: LibraryRagOrchestrationRequest,
): void {
  if (
    bundle.request.prompt !== request.prompt ||
    bundle.request.target !== request.target ||
    bundle.request.locale !== request.locale ||
    bundle.request.applicationInstanceId !== request.applicationInstanceId
  ) {
    throw new Error("platform-library-request-binding-mismatch")
  }
}

function assertManifestBinding(bundle: PlatformLibraryKnowledgeBundleV1): void {
  const manifest = CANONICAL_PLATFORM_LIBRARY_ASSET_BINDING_MANIFEST
  const blueprint = bundle.applicationBlueprint
  if (
    blueprint.ref !== manifest.applicationBlueprint.ref ||
    blueprint.schemaVersion !== manifest.applicationBlueprint.schemaVersion ||
    blueprint.sourceRef !== manifest.applicationBlueprint.sourceRef ||
    blueprint.contentHash !== manifest.applicationBlueprint.rawContentHash ||
    blueprint.applicationCoreRef !==
      NEWSLETTER_MEMBERSHIP_APPLICATION_BLUEPRINT.applicationCoreRef ||
    blueprint.userGoal !== NEWSLETTER_MEMBERSHIP_APPLICATION_BLUEPRINT.userGoal
  ) {
    throw new Error("platform-library-blueprint-authority-mismatch")
  }
  if (
    bundle.uxPatterns.length !== manifest.uxPatterns.length ||
    !bundle.uxPatterns.every((asset, index) => {
      const expected = manifest.uxPatterns[index]
      return (
        asset.ref === expected.ref &&
        asset.schemaVersion === expected.schemaVersion &&
        asset.sourceRef === expected.sourceRef &&
        asset.contentHash === expected.rawContentHash &&
        asset.intent === patternIntent(expected.ref)
      )
    })
  ) {
    throw new Error("platform-library-ux-authority-mismatch")
  }
  const design = bundle.designOntology
  if (
    design.ref !== manifest.designOntology.ref ||
    design.schemaVersion !== manifest.designOntology.schemaVersion ||
    design.sourceRef !== manifest.designOntology.sourceRef ||
    design.contentHash !== manifest.designOntology.rawContentHash ||
    design.applicationRef !==
      NEWSLETTER_MEMBERSHIP_APPLICATION_BLUEPRINT.applicationCoreRef
  ) {
    throw new Error("platform-library-design-authority-mismatch")
  }
  const expectedSources = new Set([
    manifest.applicationBlueprint.sourceRef,
    ...manifest.uxPatterns.map((asset) => asset.sourceRef),
    manifest.designOntology.sourceRef,
    ...bundle.sourceInteractionRelationSet.designSourceRefs,
    ...bundle.sourceInteractionRelationSet.evidenceRefs,
  ])
  if (!sameSet([...expectedSources], bundle.provenance.sourceRefs)) {
    throw new Error("platform-library-provenance-source-mismatch")
  }
}

function patternIntent(ref: string): string | undefined {
  const pattern = NEWSLETTER_MEMBERSHIP_UX_PATTERNS.find(
    (candidate) => `${candidate.uxPatternId}@${candidate.version}` === ref,
  )
  return pattern?.intent
}

function summary<Kind extends LibraryKnowledgeSummary["kind"]>(input: {
  kind: Kind
  ref: string
  title: string
  detail: string
  evidenceRefs: readonly string[]
}): LibraryKnowledgeSummary & { kind: Kind } {
  return {
    kind: input.kind,
    ref: input.ref,
    title: input.title,
    summary: input.detail,
    score: 1,
    evidenceRefs: [...input.evidenceRefs].sort(),
  }
}

function sameSet(left: readonly string[], right: readonly string[]): boolean {
  const a = [...new Set(left)].sort()
  const b = [...new Set(right)].sort()
  return a.length === b.length && a.every((item, index) => item === b[index])
}

function deepFreeze<T>(value: T): T {
  if (value && typeof value === "object" && !Object.isFrozen(value)) {
    Object.freeze(value)
    for (const child of Object.values(value as Record<string, unknown>)) {
      deepFreeze(child)
    }
  }
  return value
}
